// lang.tsx
// App language (English / German). The choice is remembered in localStorage;
// a first visit follows the browser language.
//
//   useLang()        { lang, setLang } inside React
//   useL(byLang)     pick the current language's value from { en, de }
//   getLang()        current language outside React (canvas code, audio)
//   pick(byLang)     same as useL, outside React
//
// Content lives next to its slide as { en: …, de: … } objects (see
// slideN.data.ts); shared UI labels are in i18n/ui.ts.
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

export type Lang = "en" | "de";
export const LANGS: Lang[] = ["en", "de"];
export type ByLang<T> = Record<Lang, T>;

const STORAGE_KEY = "dreamfulness.lang";

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "de") return saved;
  } catch (_) {}
  const nav = typeof navigator !== "undefined" ? navigator.language || "" : "";
  return nav.toLowerCase().startsWith("de") ? "de" : "en";
}

// Module-level copy so non-React code (canvas loops, audio helpers) can read it.
let current: Lang = initialLang();
export const getLang = (): Lang => current;
export const pick = <T,>(byLang: ByLang<T>, lang: Lang = current): T => byLang[lang];

const LangContext = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({
  lang: current,
  setLang: () => {},
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(current);

  const setLang = useCallback((l: Lang) => {
    current = l; // before re-render, so remounted slides read the new value
    try { localStorage.setItem(STORAGE_KEY, l); } catch (_) {}
    setLangState(l);
  }, []);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
export function useL<T>(byLang: ByLang<T>): T {
  return byLang[useLang().lang];
}
